import { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import useSectionReveal from '@/hooks/useSectionReveal';
import { footerCopy } from '@/mocks/home';
import { heroCopy } from '@/mocks/hero';
import onView from '@/hooks/onView';

const label = 'text-[11px] font-label font-medium uppercase tracking-[0.22em] text-secondary-300';
const link = 'text-[15px] text-background-100/85 hover:text-background-50 transition-colors duration-300 cursor-pointer whitespace-nowrap';

export default function SiteFooter() {
  const ref = useRef<HTMLElement>(null);
  useSectionReveal(ref);

  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      const q = gsap.utils.selector(ref);
      const offs: Array<() => void> = [];
      q('[data-f-line]').forEach((line: HTMLElement) => {
        const t = gsap.fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 1.2, ease: 'power3.inOut', paused: true });
        offs.push(onView(line, () => t.restart(), () => t.pause(0), '0px 0px -4% 0px'));
      });
      const mark = q('[data-f-mark]')[0];
      const mt = gsap.fromTo(mark, { y: 48, opacity: 0, letterSpacing: '-0.012em' }, {
        y: 0, opacity: 1, letterSpacing: '-0.035em', duration: 1.5, ease: 'power3.out', paused: true,
      });
      offs.push(onView(mark?.parentElement, () => mt.restart(), () => mt.pause(0), '0px 0px 0px 0px'));
      return () => offs.forEach((off) => off());
    });
  }, { scope: ref });

  return (
    <footer ref={ref} className="bg-primary-950 text-background-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-10 pt-16 md:pt-20 lg:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          <div className="lg:col-span-6">
            <p data-reveal className={label}>{footerCopy.descriptor}</p>
            <p data-reveal className="mt-6 font-heading font-light text-[28px] md:text-[40px] leading-[1.12] tracking-[-0.01em] text-background-50 max-w-[18ch]">
              {footerCopy.statement}
            </p>
            <a data-reveal href="#estimate" className="mt-9 inline-flex items-center gap-3 text-sm text-background-50 border-b border-background-50/30 hover:border-background-50 pb-1.5 transition-colors cursor-pointer whitespace-nowrap">
              Request a free estimate
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
            </a>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-10 lg:pl-10">
            {footerCopy.columns.map((c) => (
              <nav key={c.title} data-reveal aria-label={c.title}>
                <h4 className={label}>{c.title}</h4>
                <ul className="mt-5 space-y-3">
                  {c.links.map((l) => <li key={l.label}><a href={l.href} className={link}>{l.label}</a></li>)}
                </ul>
              </nav>
            ))}
            <div data-reveal className="col-span-2 md:col-span-1">
              <h4 className={label}>Contact</h4>
              <ul className="mt-5 space-y-3 text-[15px] text-background-100/85">
                <li><a href={`tel:${footerCopy.phone}`} className={link}>{footerCopy.phone}</a></li>
                <li><a href={`mailto:${footerCopy.email}`} className={link}>{footerCopy.email}</a></li>
                <li className="text-secondary-300 leading-relaxed max-w-[22ch]">{footerCopy.address}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="relative mt-16 md:mt-24 pt-8 md:pt-10">
          <span data-f-line aria-hidden="true" className="absolute left-0 right-0 top-0 h-px bg-background-50/10 origin-left" />
          <div className="overflow-hidden pb-[0.06em]">
            <p data-f-mark aria-hidden="true" className="nl-wordmark font-heading font-light text-background-50/90 select-none whitespace-nowrap">
              {heroCopy.brand}
            </p>
          </div>
        </div>

        <div className="relative mt-8 md:mt-10 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-secondary-300">
          <span data-f-line aria-hidden="true" className="absolute left-0 right-0 top-0 h-px bg-background-50/10 origin-right" />
          <p>© 2026 {heroCopy.brand} Windows. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="inline-flex items-center gap-1 hover:text-background-50 transition-colors duration-300 cursor-pointer whitespace-nowrap group">Instagram<span className="w-3 h-3 flex items-center justify-center transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"><i className="ri-arrow-right-up-line" /></span></a>
            <a href="#" className="inline-flex items-center gap-1 hover:text-background-50 transition-colors duration-300 cursor-pointer whitespace-nowrap group">Pinterest<span className="w-3 h-3 flex items-center justify-center transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5"><i className="ri-arrow-right-up-line" /></span></a>
            <a href="#top" className="hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">Back to top</a>
          </div>
        </div>
      </div>
    </footer>
  );
}